import { useState } from "react";
import { useTrackLinks } from "@/lib/shopper";
import { Show, UserButton } from "@clerk/react";
import { Heart } from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, Scale, TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useCompare } from "@/components/site/compare";
import { assetUrl, useSite } from "@/lib/site";
import { hasMenu, MegaMenu, MobileSection, useHideOnScroll } from "@/components/site/HeaderMenus";

const NAV = [
  ["/deals", "All Deals"],
  ["/offers/diwali", "Diwali Offers"],
  ["/coupons", "Coupons"],
  ["/stores", "Stores"],
  ["/categories", "Categories"],
  ["/guides", "Buying Guides"],
];

export const Logo = ({ light = false, size = "header" }) => {
  const site = useSite();
  const split = site.name === "BachatRaja";
  const big = size === "header";
  const src = (light && site.logo_light_url) || site.logo_url;
  return (
    <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="site-logo" aria-label={`${site.name} home`}>
      {src ? (
        <img src={assetUrl(src)} alt={site.name} data-testid="site-logo-image"
          className={`block w-auto h-auto object-contain object-left ${LOGO_FIT[size]}`} />
      ) : (
        <>
          <span className={`${big ? "h-10 w-10 sm:h-12 sm:w-12" : "h-9 w-9"} shrink-0 rounded-xl bg-emerald-600 grid place-items-center`}>
            <span className={`${big ? "h-7 w-7 sm:h-8 sm:w-8 text-base" : "h-6 w-6 text-sm"} rounded-full bg-amber-400 grid place-items-center text-slate-900 font-black`}>₹</span>
          </span>
          <span className="flex flex-col min-w-0 leading-tight">
            <span className={`font-display font-extrabold tracking-tight truncate ${big ? "text-2xl sm:text-3xl" : "text-xl"} ${light ? "text-white" : "text-slate-900"}`} data-testid="site-logo-name">
              {split ? <>Bachat<span className="text-emerald-600">Raja</span></> : site.name}
            </span>
            {big && site.tagline && <span className="text-xs sm:text-sm font-medium text-slate-500 truncate max-[399px]:hidden" data-testid="site-logo-tagline">{site.tagline}</span>}
          </span>
        </>
      )}
    </Link>
  );
};

const LOGO_FIT = {
  header: "max-h-12 sm:max-h-14 lg:max-h-16 max-w-[165px] min-[400px]:max-w-[200px] sm:max-w-[300px] lg:max-w-[360px]",
  footer: "max-h-14 max-w-[220px]",
  sidebar: "max-h-12 max-w-[200px]",
};

const TrackLinks = () => { useTrackLinks(); return null; };

const ShopperMenu = () => (
  <div className="flex items-center gap-1" data-testid="shopper-menu">
    <Show when="signed-out">
      <Link to="/sign-in" className="px-3 py-1.5 rounded-full border border-slate-300 text-sm font-semibold hover:bg-slate-50 whitespace-nowrap" data-testid="header-sign-in">Sign in</Link>
    </Show>
    <Show when="signed-in">
      <TrackLinks />
      <Link to="/account" className="p-2 rounded-full hover:bg-slate-100" aria-label="Saved deals" data-testid="header-saved-link"><Heart size={20} /></Link>
      <UserButton />
    </Show>
  </div>
);

const logSearch = (q) => q && api.post("/public/search-log", { q }).catch(() => {});

const Trending = ({ onPick }) => {
  const { data } = useQuery({ queryKey: ["trending"], queryFn: () => api.get("/public/trending").then((r) => r.data), staleTime: 5 * 60 * 1000 });
  if (!data?.length) return null;
  return (
    <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white rounded-2xl border border-slate-200 shadow-xl p-3" data-testid="trending-panel"
      onMouseDown={(e) => e.preventDefault()}>
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2"><TrendingUp size={14} /> Trending searches</p>
      <div className="flex flex-wrap gap-2">
        {data.map((t) => (
          <button type="button" key={t} onClick={() => onPick(t)} data-testid={`trending-term-${t.replace(/[^a-z0-9]+/g, "-")}`}
            className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-sm font-semibold text-slate-700 transition-colors">{t}</button>
        ))}
      </div>
    </div>
  );
};

const SearchBox = ({ testid, onDone }) => {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const nav = useNavigate();
  const go = (term) => {
    logSearch(term);
    nav(`/deals?q=${encodeURIComponent(term)}`);
    setFocus(false);
    document.activeElement?.blur?.();
    onDone?.();
  };
  const submit = (e) => {
    e.preventDefault();
    go(q.trim());
  };
  return (
    <form onSubmit={submit} className="relative w-full" role="search">
      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onKeyDown={(e) => e.key === "Escape" && setFocus(false)}
        placeholder="Search deals, brands, stores…"
        aria-label="Search deals"
        data-testid={testid}
        className="w-full h-11 pl-10 pr-4 rounded-full bg-slate-100 border border-transparent focus:border-emerald-500 focus:bg-white outline-none text-sm transition-colors"
      />
      {focus && !q.trim() && <Trending onPick={(t) => { setQ(t); go(t); }} />}
    </form>
  );
};

const MainNav = () => (
  <nav className="hidden md:block border-t border-slate-100" aria-label="Main">
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center gap-1 text-sm font-semibold">
      {NAV.map(([to, label]) => {
        const link = (
          <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) => `px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`}>
            {label}
          </NavLink>
        );
        return hasMenu(to) ? <MegaMenu key={to} path={to}>{link}</MegaMenu> : link;
      })}
    </div>
  </nav>
);

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="md:hidden p-2 rounded-full hover:bg-slate-100" aria-label="Open menu" data-testid="mobile-menu-button"><Menu size={22} /></button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-white w-80 overflow-y-auto">
        <SheetTitle className="font-display">Menu</SheetTitle>
        <div className="mt-6 space-y-4">
          <SearchBox testid="mobile-search-input" onDone={() => setOpen(false)} />
          {NAV.concat([["/compare", "Compare"], ["/about", "About Us"]]).map(([to, label]) => (
            <div key={to}>
              <Link to={to} onClick={() => setOpen(false)} className="block py-2 font-semibold border-b border-slate-100" data-testid={`mobile-nav-${to.slice(1)}`}>{label}</Link>
              {hasMenu(to) && <MobileSection path={to} onPick={() => setOpen(false)} />}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const { ids } = useCompare();
  const hideTop = useHideOnScroll();
  return (
    <header className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 transition-transform duration-300 ${hideTop ? "max-sm:-translate-y-16 sm:max-md:-translate-y-20" : ""}`}
      data-testid="site-header" data-top-hidden={hideTop ? "true" : "false"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center gap-3 sm:gap-6" data-testid="header-top-row">
        <Logo />
        <div className="hidden md:block flex-1 max-w-xl mx-auto"><SearchBox testid="header-search-input" /></div>
        <div className="ml-auto md:ml-0 flex items-center gap-1 shrink-0">
          <ShopperMenu />
          <Link to="/compare" data-testid="header-compare-link" className="relative p-2 rounded-full hover:bg-slate-100" aria-label="Compare">
            <Scale size={20} />
            {ids.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-bold h-4 w-4 rounded-full grid place-items-center">{ids.length}</span>}
          </Link>
          <MobileMenu />
        </div>
      </div>
      <div className="md:hidden px-4 pb-3"><SearchBox testid="header-search-input-mobile" /></div>
      <MainNav />
    </header>
  );
};
